import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Body } from '~/components/atom/Typography';
import { WizardStepProps } from '~/components/organism/Wizard/context/WizardContext';
import useWizardStep from '~/hooks/useWizardStep';
import { OutputMap } from '../..';
import Loading from '~/components/atom/Loading';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import useContextAwareToggle from '~/hooks/useContextAwareToggle';
import JustificationContainer from '~/components/atom/JustificationContainer';

const text = {
  title: 'Slimme weergave',
  description:
    'Schakel slimme weergave voor de widget in om automatisch de app met de hoogste conversiekans te tonen op basis van de bezoeker en de pagina die ze bekijken. Dit kan vastgezette apps vervangen.',
  toggleLabel:
    'Toon automatisch de app met de hoogste conversiekans op basis van de bezoeker en de pagina die ze bekijken.',
};

export const title = text.title;
export const id = 'ContextAwareToggle';

export type OutputType = {
  type: typeof id;
};

export const Component: React.FCC<WizardStepProps> = props => {
  const { queryLoading, updateLoading, onChange } = useContextAwareToggle();
  /** We want them to turn it on by default */
  const [value, setValue] = useState<boolean>(true);

  const onBeforeNext = useCallback(
    async (outputMap: OutputMap) => {
      const currentOutput = outputMap[id] as OutputType;

      // Only update the setting if the user has enabled the toggle, initial value will be false anyway
      if (value) {
        await onChange(value);
      }

      return currentOutput;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );

  const stepOptions = useMemo(
    () => ({
      onBeforeNext,
    }),
    [onBeforeNext],
  );

  const [, api] = useWizardStep(props.step.id, stepOptions);

  useEffect(() => {
    api.free({ type: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (newValue: boolean) => {
    setValue(newValue);
  };

  if (queryLoading) {
    return <Loading />;
  }

  return (
    <JustificationContainer direction="column" margin={[null, null, 'l', null]}>
      <Body margin={[null, null, 'm', null]}>{text.description}</Body>
      <ToggleCheckbox
        label={text.toggleLabel}
        value={value}
        loading={updateLoading}
        onChange={() => handleChange(!value)}
        containerProps={{
          gap: 's',
          align: 'center',
          justification: 'center',
          width: '100%',
        }}
        withAI
      />
    </JustificationContainer>
  );
};

export default {
  title,
  id,
};
